import { useEffect } from 'react'
import { useSignalR } from './useSignalR'
import { toCamel } from '../utils/helpers/object.helpers'
import useProjectTimestamp from './useProjectTimestamp'
import useProjectOrderlineDetail from './useProjectOrderlineDetail'
import {
  PmrE2EProjectLineUpdateStatusResult,
  PmrE2EProjectLineUpdateStatusResultModel,
} from '../pages/project-management-pmr/shared/interfaces/pmr-e2e-project-details-orderline-model'
import {
  selectedProjectManagementViewProjects,
  selectExpandedRowsProjectList,
  updateE2EProjectsStatuses,
  updateIsMismatchECD,
  updateProjectManagementViewProjectsAdhocStatus,
  updateRefreshProjectId,
  updateProjectOverride,
  updateTaskAddedOnProjectId,
  upsertOrderlineDetailsListFromUpdateStatus,
} from '../features/projectManagement/projectManagementSlice'
import { useDispatch, useSelector } from 'react-redux'
import * as _ from 'lodash'
import { IPmrGpiWorkflowCreationModel } from '../pages/project-management-pmr/shared/interfaces/pmr-e2e-create-gpi-workflow-model'
import useToastr from './useToastr'
import { IPmrProjectAdhocTaskSyncModel } from '../pages/project-management-pmr/shared/interfaces/pmr-e2e-project-adhoc-task-sync-model'
import { updateRefreshInvoiceWarnings } from '../features/projectManagementPmr/projectManagementPmrSlice'
import ProjectOrderlineStatus from '../pages/project-management-pmr/shared/enums/project-orderline-status'

const useProjectManagementPmrSignalr = () => {
  const dispatch = useDispatch()
  const expandedRows = useSelector(selectExpandedRowsProjectList)
  const projectManagementViewProjects = useSelector(selectedProjectManagementViewProjects)
  const { showSuccess } = useToastr()

  const milestoneTimestampsHubUrl = (process.env.REACT_APP_GMAE2E_URL +
    '/hubs/milestone_timestamps') as string

  const { connection: milestoneTimestampsConnection } = useSignalR(milestoneTimestampsHubUrl)
  const projectLineUpdateStatusHubUrl = (process.env.REACT_APP_GMAE2E_URL +
    '/hubs/projectline_updatestatus') as string

  const { connection: projectLineUpdateStatusConnection } = useSignalR(
    projectLineUpdateStatusHubUrl
  )

  const projectStatusUpdates = (process.env.REACT_APP_GMAE2E_URL +
    '/hubs/project_updatestatus') as string

  const { connection: projectStatusUpdatesSignalRConnection } = useSignalR(projectStatusUpdates)

  const functionHubUrl = (process.env.REACT_APP_GMAE2E_FUNC_URL + '/api') as string
  const { connection: functionSignalRConnection } = useSignalR(functionHubUrl)

  const projectLineIsMismatchECD = (process.env.REACT_APP_GMAE2E_URL +
    '/hubs/projectline_ismismatch_ecd_update') as string
  const { connection: projectLineIsMismatchECDConnection } = useSignalR(projectLineIsMismatchECD)

  const projectTimestampHook = useProjectTimestamp()
  const projectOrderlineDetailHook = useProjectOrderlineDetail()

  useEffect(() => {
    if (milestoneTimestampsConnection) {
      milestoneTimestampsConnection.on('finished', result => {
        if (result) {
          console.log(`milestoneTimestampsConnection finished`)
          console.log(`milestoneTimestampsConnection finished`, result)
          const parsedResult = toCamel(JSON.parse(result))
          projectTimestampHook.onTimestampFinished(parsedResult)
        }
      })
      milestoneTimestampsConnection.on('projectLineStatusWritebackInprogress', result => {
        if (result) {
          //TODO:To remove for debugging purposes on dev
          console.log(`milestoneTimestampsConnection projectLineStatusWritebackInprogress`)
          console.log(`milestoneTimestampsConnection projectLineStatusWritebackInprogress`, result)
          const results: PmrE2EProjectLineUpdateStatusResult[] = toCamel(JSON.parse(result))
          projectOrderlineDetailHook.applySignalrOrderlineStatusWritebackInprogress(results)
        }
      })
      milestoneTimestampsConnection.on('japanForOrderLineCompletion', result => {
        if (result) {
          //TODO:To remove for debugging purposes on dev
          console.log(`milestoneTimestampsConnection japanForOrderLineCompletion`)
          console.log(`milestoneTimestampsConnection japanForOrderLineCompletion`, result)
          const results: PmrE2EProjectLineUpdateStatusResultModel = toCamel(JSON.parse(result))
          projectOrderlineDetailHook.applySignalrJapanOrderLineForCompletion(results)
        }
      })
      milestoneTimestampsConnection.on('japanForFlexProjectCompletion', result => {
        if (result) {
          //TODO:To remove for debugging purposes on dev
          console.log(`milestoneTimestampsConnection japanForFlexProjectCompletion`)
          console.log(`milestoneTimestampsConnection japanForFlexProjectCompletion`, result)
          const results: PmrE2EProjectLineUpdateStatusResultModel = toCamel(JSON.parse(result))
          projectOrderlineDetailHook.applySignalrJapanFlexProjectForCompletion(results)
        }
      })
    }
  }, [milestoneTimestampsConnection])

  useEffect(() => {
    if (projectStatusUpdatesSignalRConnection) {
      projectStatusUpdatesSignalRConnection.on('projectStatusUpdated', result => {
        if (result) {
          const parsedResult = toCamel(JSON.parse(result))
          dispatch(updateE2EProjectsStatuses(parsedResult))
        }
      })
    }
  }, [projectStatusUpdatesSignalRConnection])

  useEffect(() => {
    if (projectLineUpdateStatusConnection) {
      projectLineUpdateStatusConnection.on('projectLineStatusUpdated', result => {
        if (result) {
          //currently no implementation
        }
      })
    }
  }, [projectLineUpdateStatusConnection])

  useEffect(() => {
    if (functionSignalRConnection) {
      functionSignalRConnection.on('project-line-status-updated', result => {
        if (result) {
          //TODO:To remove for debugging purposes on dev
          console.log(`functionSignalRConnection project-line-status-updated`)
          console.log(`functionSignalRConnection project-line-status-updated`, result)
          const resultModel: PmrE2EProjectLineUpdateStatusResultModel = toCamel(JSON.parse(result))
          projectOrderlineDetailHook.applySignalrFlexProjectLineStatusUpdate(resultModel)
        }
      })
      functionSignalRConnection.on('flex-project-status-updated', result => {
        if (result) {
          //TODO:To remove for debugging purposes on dev
          console.log(`functionSignalRConnection flex-project-status-updated`)
          console.log(`functionSignalRConnection flex-project-status-updated fromMessage`, result)
          const resultModel: PmrE2EProjectLineUpdateStatusResultModel = toCamel(JSON.parse(result))
          projectOrderlineDetailHook.applySignalrFlexProjectStatusUpdate(resultModel)
        }
      })

      functionSignalRConnection.on('project-line-ecd-updated', (result: any) => {
        if (result) {
          let params = JSON.parse(result)

          const e2EProjectId = params.e2EProjectId
          const e2EProjectLineId = params.e2EProjectLineId

          if (params.isUpdateOrderLineEcd) {
            setProjectLineIsMismatchECD(
              e2EProjectLineId,
              e2EProjectId,
              params.isUpdateOrderLineEcd,
              params.isUpdateOrderLineEcd,
              params.newECD,
              params.isUpdateOrderLineEcd
            )
          }

          if (params.isUpdateOrderLineStatus) {
            setProjectLineStatus(
              e2EProjectLineId,
              params.fulfillmentStatusDetail,
              params.orderLineStatus
            )
          }
        }
      })

      functionSignalRConnection.on('gpi-workflow-creation-success', result => {
        if (result) {
          console.log(`functionSignalRConnection GPI and Workflow Creation Successful`)

          const resultModel: IPmrGpiWorkflowCreationModel = toCamel(JSON.parse(result))

          if (expandedRows[resultModel.e2EProjectId]) {
            showSuccess(
              'DM Integrations Ready',
              'DM integrations are complete. Icons are now enabled and ready for use.'
            )
          }

          dispatch(updateRefreshProjectId(resultModel.e2EProjectId))
        }
      })

      functionSignalRConnection.on('project-adhoc-sync-success', result => {
        if (result) {
          console.log(`functionSignalRConnection Adhoc Task Sync Successful`)
          const resultModel: IPmrProjectAdhocTaskSyncModel = toCamel(JSON.parse(result))

          if (expandedRows[resultModel.e2EProjectId] && resultModel.hasAdhocTasks === true) {
            showSuccess(
              'Ad Hoc Tasks Setup Complete',
              `Ad hoc tasks for the project <b>'${resultModel.e2EProjectName}'</b> have been successfully set up.`
            )
          }

          dispatch(updateTaskAddedOnProjectId(resultModel.e2EProjectId))
          dispatch(updateProjectManagementViewProjectsAdhocStatus(resultModel.e2EProjectId))
        }
      })

      functionSignalRConnection.on('payment-completed-without-invoice-price', result => {
        if (!result) return

        console.log(`functionSignalRConnection FLEX UI task without invoice price`)
        dispatch(updateRefreshInvoiceWarnings(true))
      })

      functionSignalRConnection.on('bcp-project-override', result => {
        if (result) {
          //TODO:To remove for debugging purposes on dev
          var resultModel = JSON.parse(result)
          dispatch(updateProjectOverride(resultModel))
        }
      })
    }
  }, [functionSignalRConnection])

  useEffect(() => {
    if (projectLineIsMismatchECDConnection) {
      projectLineIsMismatchECDConnection.on(
        'project-line-ismismatch-ecd-updated-success',
        params => {
          if (params) {
            setProjectLineIsMismatchECD(
              params.e2EProjectLineId,
              params.e2EProjectId,
              false,
              params.e2EProjectHasMismatchECD
            )
          }
        }
      )
    }
  }, [projectLineIsMismatchECDConnection])

  const setProjectLineIsMismatchECD = (
    orderLineId: string,
    projectId: string,
    status: boolean,
    projectHasMismatchECD: boolean,
    newECD?: string,
    isUpdateOrderLineEcd?: boolean
  ) => {
    dispatch(
      updateIsMismatchECD({
        e2eProjectId: projectId,
        e2EProjectLineId: orderLineId,
        isMismatchECD: status,
        e2EProjectHasMismatchECD: projectHasMismatchECD,
        newECD: newECD,
        isUpdateOrderLineEcd,
      })
    )
  }

  const setProjectLineStatus = (
    e2EProjectLineId: string,
    statusDetail: string,
    status: ProjectOrderlineStatus
  ) => {
    dispatch(
      upsertOrderlineDetailsListFromUpdateStatus({
        id: e2EProjectLineId,
        fulFillmentStatusDetail: statusDetail,
        fulFillmentStatusDetailId: statusDetail,
        orderLineStatus: status,
      })
    )
  }

  return {}
}
export default useProjectManagementPmrSignalr
